import { firmLongName, firmShortLabel } from "./firmDisplayName";

/** Cosmos id for the internal World Services Group entity. */
export const WORLD_SERVICES_GROUP_FIRM_ID =
  "6ed82f4e-6b67-407f-a698-05281b24095a";

/** Staff Add Participant firm picker — exact Cosmos ids only. */
export const STAFF_REGISTRATION_FIRM_IDS = new Set([
  "430be9f8-c9be-4f62-aec1-91e0596799b8", // Test Demo
  WORLD_SERVICES_GROUP_FIRM_ID,
]);

/** WSG internal / demo firms — excluded from member-facing selection (attendees, send-email). */
export const WSG_STAFF_FIRM_IDS = new Set([
  "ee153da4-bbf0-4c29-80cb-29a05905ae2c",
  "430be9f8-c9be-4f62-aec1-91e0596799b8",
  "7cef821b-c7f7-40a8-bf7c-93aab29ac54d",
  "6ed82f4e-6b67-407f-a698-05281b24095a",
]);

/** Legacy firm ids for WSG (3021) and Test Demo t (10634). */
export const WSG_STAFF_FIRM_LEGACY_IDS = new Set(["3021", "10634"]);

const EXCLUDED_FIRM_NAME_PATTERNS = [
  /^world services group\b/i,
  /^test demo\b/i,
];

export type FirmIdentityInput = {
  firmId?: string | null;
  legacyId?: string | null;
  firmName?: string | null;
};

function normalizeId(value?: string | null): string {
  return String(value ?? "").trim();
}

export function isWorldServicesGroupFirmId(firmId?: string | null): boolean {
  return normalizeId(firmId) === WORLD_SERVICES_GROUP_FIRM_ID;
}

export function isExcludedStaffOrDemoFirm(input: FirmIdentityInput): boolean {
  const firmId = normalizeId(input.firmId);
  const legacyId = normalizeId(input.legacyId);

  if (firmId && WSG_STAFF_FIRM_IDS.has(firmId)) {
    return true;
  }
  if (legacyId && WSG_STAFF_FIRM_LEGACY_IDS.has(legacyId)) {
    return true;
  }
  if (firmId && WSG_STAFF_FIRM_LEGACY_IDS.has(firmId)) {
    return true;
  }

  const firmName = normalizeId(input.firmName);
  if (
    firmName &&
    EXCLUDED_FIRM_NAME_PATTERNS.some((pattern) => pattern.test(firmName))
  ) {
    return true;
  }

  return false;
}

/** Staff Add Participant: only the two configured internal firms (by firm id). */
export function isStaffRegistrationFirm(input: FirmIdentityInput): boolean {
  const firmId = normalizeId(input.firmId);
  return firmId.length > 0 && STAFF_REGISTRATION_FIRM_IDS.has(firmId);
}

export function isMemberFirmRecipient(firmId?: string | null): boolean {
  const id = normalizeId(firmId);
  if (!id) {
    return false;
  }
  return !isExcludedStaffOrDemoFirm({ firmId: id });
}

export function isMemberFacingEventAttendee(input: {
  attendeeType?: string | null;
  firmId?: string | null;
  firmName?: string | null;
  personFirmId?: string | null;
  firmLegacyId?: string | null;
  firmDisplayName?: string | null;
}): boolean {
  const attendeeType = normalizeId(input.attendeeType).toLowerCase();
  if (attendeeType === "staff") {
    return false;
  }

  return !isExcludedStaffOrDemoFirm({
    firmId: input.firmId || input.personFirmId,
    legacyId: input.firmLegacyId,
    firmName: input.firmName || input.firmDisplayName,
  });
}

type FirmListItem = {
  id: string;
  name?: string;
  firmName?: string;
  shortName?: string;
  longName?: string;
  legacyId?: string;
  shortId?: string | number;
};

function firmIdentityFromListItem(firm: FirmListItem): FirmIdentityInput {
  const legacyId =
    firm.legacyId ??
    (firm.shortId != null && String(firm.shortId).trim() !== ""
      ? String(firm.shortId)
      : undefined);

  return {
    firmId: firm.id,
    legacyId,
    firmName: firm.firmName || firm.longName || firm.name,
  };
}

/** Staff registrants: WSG + Test Demo only. Everyone else: member firms only. */
export function filterFirmsForRegistrantType(
  firms: FirmListItem[],
  attendeeType?: string | null,
): { id: string; name: string; longName: string }[] {
  const isStaff = normalizeId(attendeeType).toLowerCase() === "staff";
  const toOption = (firm: FirmListItem) => ({
    id: firm.id,
    name: firmShortLabel(firm) || "Unknown Firm",
    longName: firmLongName(firm),
  });

  if (isStaff) {
    return firms
      .filter((firm) => STAFF_REGISTRATION_FIRM_IDS.has(firm.id))
      .map(toOption);
  }

  return firms
    .filter((firm) => {
      const identity = firmIdentityFromListItem(firm);
      return !isExcludedStaffOrDemoFirm(identity);
    })
    .map(toOption);
}
